import type { PublicMeasurementResponseRecord } from '@on3/api';
import { Check } from '@on3/ui-lib/components/Svg';
import { epochDateConverter } from '@on3/ui-lib/utils/epochDateConverter';
import { formatEnumToLabel } from '@on3/ui-lib/utils/formatters';
import clsx from 'clsx';
import { TeamAvatar } from 'components/Image/TeamAvatar';
import { Text } from 'components/UI/Typography/Text';
import useAthlete from 'contexts/AthleteProvider';
import { useCallback } from 'react';

import styles from './PlayerVitalsList.module.scss';
import {
  keysToConvertToFeet,
  RecordCode,
  RecordStyles,
} from './TrackAndField/TrackAndFieldItem';

export const PlayerVital = ({
  measurementTypeKey,
  type,
  measurementType,
  value,
  verified,
  organization,
  ageMeasurementOccurred,
  top300Average,
  delta,
  draftChangePercent,
  draftedAverage,
  topAverageChangePercent,
  event,
  record,
  dateOccurred,
}: PublicMeasurementResponseRecord) => {
  const { athlete } = useAthlete();
  const is40 = measurementTypeKey === 6;
  const isHeight = measurementTypeKey === 1;
  const isLowest = measurementType?.sort === 'Lowest';
  const isInchesToFeet =
    measurementTypeKey && keysToConvertToFeet.includes(measurementTypeKey);
  const isTimeFormat = measurementType?.units === 'secs';
  const recordTitle = record && formatEnumToLabel(record);
  const isPositiveForPlayer = () => {
    if (delta) {
      if (isLowest) {
        return delta < 0;
      }

      return delta > 0;
    }

    return false;
  };
  /**Calculates the percential the current measurement is compared to the avg:(top300 or drafted) */
  const calculatePercentDifference = (difference: number) => {
    const isPositive = isLowest ? difference < 100 : difference > 100;
    const absoluteDifference = Math.abs(difference - 100);
    const percentile = isPositive
      ? 100 + absoluteDifference
      : 100 - absoluteDifference;

    return (
      <span
        className={clsx(styles.difference, {
          [styles.positive]: isPositive,
          [styles.negative]: !isPositive,
        })}
      >
        {percentile.toFixed(2)}%
      </span>
    );
  };

  const formattedValue = useCallback(
    (v: number) => {
      if (isInchesToFeet || isHeight) {
        const inches = v;
        const feet = Math.floor(inches / 12);
        const remainingInches = inches % 12;

        const formattedInches = `${remainingInches
          .toFixed(1)
          .replace(/\.?0+$/, '')}`;

        if (isHeight) {
          return `${feet}-${formattedInches}`;
        }

        return `${feet}'${formattedInches}"`;
      }

      if (isTimeFormat) {
        const minutes = Math.floor(v / 60);
        const remainingSeconds = Math.floor(v % 60);
        const milliseconds = Math.floor((v % 1) * 100);

        let timeString = '';

        if (minutes > 0) {
          timeString += minutes.toString() + ':';
        }

        timeString +=
          remainingSeconds.toString() + '.' + milliseconds.toString();

        return timeString;
      }

      return parseFloat(v.toFixed(2));
    },
    [isHeight, isInchesToFeet, isTimeFormat],
  );

  return (
    <li className={styles.vitalContainer}>
      <div className={styles.typeAgeContainer}>
        <div className={styles.vitalType}>
          <Text className={styles.vitalName} component="span">
            {type}
            {is40 && '-time'}
          </Text>
          {record !== 'None' && (
            <Text
              className={clsx(
                styles.record,
                styles[
                  RecordStyles[record as keyof typeof RecordStyles]
                ] as keyof typeof styles,
              )}
              component="span"
              title={recordTitle}
            >
              {RecordCode[record as keyof typeof RecordCode]}
            </Text>
          )}
          {verified && (
            <span className={styles.checkmarkContainer}>
              <Check className={styles.checkmark} />
            </span>
          )}
        </div>
        <div className={styles.dateAgeContainer}>
          {!!dateOccurred && (
            <span className={styles.date}>
              {epochDateConverter(dateOccurred, 'M/d/yyyy')}
            </span>
          )}
          {ageMeasurementOccurred && (
            <div className={styles.age}>
              <span>Age:</span>
              <span>{ageMeasurementOccurred.toFixed(1)}</span>
            </div>
          )}
        </div>
      </div>
      <Text className={styles.vitalInfo} component="div">
        <span className={styles.level}>
          <div className={styles.teamContainer}>
            {organization?.orgType !== 'HighSchool' && (
              <TeamAvatar
                height={30}
                slug={organization?.slug}
                src={organization?.assetUrl}
                width={30}
              />
            )}
            {organization?.orgType === 'HighSchool' && (
              <Text
                aria-label="High school"
                className={styles.highSchoolLabel}
                variant="subtitle1"
              >
                H.S.
              </Text>
            )}
            <span>{organization?.fullName}</span>
          </div>
        </span>
        <div className={styles.valueContainer}>
          <span className={styles.value}>{value && formattedValue(value)}</span>
          {!!delta && (
            <span
              className={clsx(styles.difference, {
                [styles.positive]: isPositiveForPlayer,
                [styles.negative]: !isPositiveForPlayer,
              })}
            >
              {`${delta > 0 ? '+' : ''}${parseFloat(
                delta.toFixed(2),
              )} ${measurementType?.units}`}
            </span>
          )}
        </div>

        <div className={styles.top300Container}>
          <span className={styles.lblMobile}>
            Top 300 {athlete?.positionAbbreviation} Avg
          </span>
          {!!top300Average && <span>{formattedValue(top300Average)}</span>}
          {topAverageChangePercent &&
            calculatePercentDifference(topAverageChangePercent)}
        </div>

        <div className={styles.draftedAvgContainer}>
          <span className={styles.lblMobile}>
            {athlete?.defaultSport?.key === 1 ? 'NFL' : 'NBA'} Combine Drafted
          </span>
          {!!draftedAverage && <span>{formattedValue(draftedAverage)}</span>}
          {draftChangePercent && calculatePercentDifference(draftChangePercent)}
        </div>
        {event && (
          <div className={styles.eventContainer}>
            <span className={styles.eventName}>{event.name}</span>
            <span className={styles.eventCity}>
              {event?.city?.name}, {event?.city?.state?.abbreviation}
            </span>
          </div>
        )}
      </Text>
    </li>
  );
};
